// ENG-2277 (HTML Forms): an image added to a Form is stored as a data URI at print quality (at most
// 200 dpi of its printed size) with its printed size in inches, so the template stays small and the
// PDF never fetches anything (tech plan D12).
import type { Editor } from "tinymce";

export type SizeInches = { width: number; height: number };
export type SizePixels = { width: number; height: number };

export const FORM_IMAGE_MAX_DPI = 200;
// Below this at printed size an image looks soft on paper; the PDF check reports it.
export const FORM_IMAGE_LOW_DPI = 150;

const CSS_PX_PER_INCH = 96;
const JPEG_QUALITY = 0.82;
// Decoding a larger file risks stalling the tab; nothing a form needs is this big.
const MAX_SOURCE_MB = 20;

const roundInches = (value: number) => Math.round(value * 1000) / 1000;

/** The pixel size to store: at most 200 dpi of the printed size, never more than the source. */
export const targetPixelSize = (
  printed: SizeInches,
  source: SizePixels
): SizePixels => {
  const scale = Math.min(
    1,
    (printed.width * FORM_IMAGE_MAX_DPI) / source.width,
    (printed.height * FORM_IMAGE_MAX_DPI) / source.height
  );
  return {
    width: Math.max(1, Math.round(source.width * scale)),
    height: Math.max(1, Math.round(source.height * scale))
  };
};

/** The image's pixels per printed inch. */
export const printDpi = (pixelWidth: number, printedWidthIn: number) =>
  printedWidthIn > 0 ? pixelWidth / printedWidthIn : 0;

export const chooseImageEncoding = (transparent: boolean) =>
  transparent
    ? ({ type: "image/png" } as const)
    : ({ type: "image/jpeg", quality: JPEG_QUALITY } as const);

/** Whether any pixel in RGBA data is not fully opaque. */
export const hasTransparency = (rgba: Uint8ClampedArray) => {
  for (let alpha = 3; alpha < rgba.length; alpha += 4) {
    if (rgba[alpha] < 255) return true;
  }
  return false;
};

/** An inserted image prints at the size the editor shows it, shrunk to fit between the margins. */
export const insertedImagePrintSize = (
  natural: SizePixels,
  maxWidthIn: number
): SizeInches => {
  const width = natural.width / CSS_PX_PER_INCH;
  const scale = Math.min(1, maxWidthIn / width);
  return {
    width: roundInches(width * scale),
    height: roundInches((natural.height / CSS_PX_PER_INCH) * scale)
  };
};

export type SourceRegion = SizePixels & { x: number; y: number };

/** An image stored for print carries its printed size in inches; one just added has no size, or a pixel one. */
export const isStoredForPrintImage = (image: HTMLImageElement) =>
  /in$/.test(image.style.width.trim());

/**
 * Whether the editor may upload this image (TinyMCE's `editorUpload.addFilter`). TinyMCE turns every
 * data: image in the content into a blob and uploads it again on every load, and maps each upload's
 * result back to an image by position after dropping the blobs it has already uploaded. An image
 * already stored for print is kept out of that cycle altogether: re-encoding it would lose quality
 * each time, re-sizing it from its pixels would change its printed size, and an upload it took no
 * part in can never land its result on it (ENG-2421). A new image still uploads once, then carries
 * its size in inches and is left alone.
 */
const shouldUpload = (image: HTMLImageElement) => !isStoredForPrintImage(image);

/**
 * Browser only (canvas): a region of an image, stored at no more than 200 dpi of its printed size,
 * as a data URI — JPEG when it is opaque, PNG when it has transparency.
 */
export const encodeForForm = (
  source: CanvasImageSource,
  region: SourceRegion,
  printed: SizeInches
) => {
  const pixels = targetPixelSize(printed, region);
  const canvas = document.createElement("canvas");
  canvas.width = pixels.width;
  canvas.height = pixels.height;
  const context = canvas.getContext("2d");
  if (!context) throw new Error("Canvas is not available");
  context.drawImage(
    source,
    region.x,
    region.y,
    region.width,
    region.height,
    0,
    0,
    pixels.width,
    pixels.height
  );
  const { data } = context.getImageData(0, 0, pixels.width, pixels.height);
  const encoding = chooseImageEncoding(hasTransparency(data));
  return encoding.type === "image/jpeg"
    ? canvas.toDataURL(encoding.type, encoding.quality)
    : canvas.toDataURL(encoding.type);
};

// Browser only (canvas): re-encodes the image at its stored pixel size. With no width to fit in,
// the image keeps the size the editor shows it at.
const downscaleForForm = async (blob: Blob, maxWidthIn: number | undefined) => {
  const bitmap = await createImageBitmap(blob);
  try {
    const printed = insertedImagePrintSize(
      bitmap,
      maxWidthIn ?? Number.POSITIVE_INFINITY
    );
    const dataUri = encodeForForm(
      bitmap,
      { x: 0, y: 0, width: bitmap.width, height: bitmap.height },
      printed
    );
    return { dataUri, printed };
  } finally {
    bitmap.close();
  }
};

/**
 * The editor's image upload for Form mode: every way an image arrives (picker, paste, drop) goes
 * through it, and `shouldUpload` keeps the images already stored for print out of it. It returns
 * the downscaled data URI, then writes the printed size in inches onto the image once TinyMCE has
 * swapped that URI in. The width between the page's margins is read as each image arrives: the
 * editor takes this handler once, when it starts, and the margins can change after that.
 */
export const createFormImageUpload = (
  getEditor: () => Editor | null | undefined,
  getMaxWidthIn: () => number | undefined
) => {
  const pendingSizes = new Map<string, SizeInches>();

  const applyPendingSizes = () => {
    const editor = getEditor();
    if (!editor || pendingSizes.size === 0) return;
    editor.dom.select("img").forEach((image) => {
      const src = image.getAttribute("src") ?? "";
      const size = pendingSizes.get(src);
      if (!size) return;
      editor.dom.setStyles(image, {
        width: `${size.width}in`,
        height: `${size.height}in`
      });
      pendingSizes.delete(src);
    });
  };

  const uploadHandler = (
    blobInfo: { blob: () => Blob; blobUri: () => string; base64: () => string },
    progress: (percent: number) => void
  ): Promise<string> => {
    const blob = blobInfo.blob();
    if (blob.size > MAX_SOURCE_MB * 1024 * 1024) {
      // TinyMCE's rejection shape: `remove` takes the failed image out of the document.
      return Promise.reject({
        message: `Images can be at most ${MAX_SOURCE_MB} MB.`,
        remove: true
      });
    }
    return downscaleForForm(blob, getMaxWidthIn()).then(
      ({ dataUri, printed }) => {
        pendingSizes.set(dataUri, printed);
        progress(100);
        // TinyMCE puts the returned URI on the image when this promise settles; size it right after.
        setTimeout(applyPendingSizes, 0);
        return dataUri;
      }
    );
  };

  return { uploadHandler, applyPendingSizes, shouldUpload };
};
